<template>
  <HeaderBar />

  <ButtonBackContainer>
    <ButtonBack :fallback="{ name: 'invoiceList' }">{{ t('back') }}</ButtonBack>
    <AuditLogButton :entity-type="AuditLogEntityType.INVOICE" :entity-id="invoiceId" />
  </ButtonBackContainer>

  <section class="carvia-container flex flex-col justify-center">
    <InvoiceForm
      v-if="invoice"
      :key="invoice.id"
      :invoice="invoice"
      :is-saving="isUpdating"
      :is-fetching="isFetching"
      @on-submit="onSubmit"
      @on-cancel="onCancel"
      @refetch="refetch"
    />
    <div v-else class="flex justify-center"><Spinner /></div>
  </section>
</template>

<script lang="ts" setup>
import ButtonBack from '@/components/buttons/ButtonBack.vue';
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import { InvoiceType } from '@/entities/invoices/invoice-type.enum';
import { InvoiceStatus } from '@/entities/invoices/invoice-status.enum';
import type { UpdateInvoiceDto } from '@/entities/invoices/invoice.entity';
import { formatDate } from '@/hooks/use-formatted-date';
import { useI18n } from 'vue-i18n';
import { useInvoice, useUpdateInvoice } from '@/queries/use-invoices';
import router from '@/router';
import { useUiStore } from '@/stores/ui.store';
import { DateTime } from 'luxon';
import { computed, watchEffect } from 'vue';
import { useRoute } from 'vue-router';
import InvoiceForm from './InvoiceForm.vue';
import ButtonBackContainer from '@/components/ButtonBackContainer.vue';
import AuditLogButton from '@/components/audit-log/AuditLogButton.vue';
import { AuditLogEntityType } from '@carvia/ros-client-types';

const { t } = useI18n();
const uiStore = useUiStore();
const route = useRoute();
const invoiceId = computed(() => route.params.id as string);

const { data: invoice, refetch, isFetching } = useInvoice(invoiceId);
const { mutateAsync: updateInvoice, isPending: isUpdating } = useUpdateInvoice();

watchEffect(() => {
  if (invoice.value) {
    const { completeInvoiceNumber, invoiceDate, type } = invoice.value;
    const docLabel =
      type === InvoiceType.SELF_BILLING ? t('invoiceTypes.SELF_BILLING') : t('invoice');
    uiStore.setHeaderTitle(
      docLabel,
      completeInvoiceNumber,
      formatDate(invoiceDate, DateTime.DATE_MED),
    );
  } else {
    uiStore.setHeaderTitle(t('invoice'));
  }
});

const onSubmit = (invoice: UpdateInvoiceDto) => {
  updateInvoice({ id: invoiceId.value, invoice });
};

const onCancel = async () => {
  await updateInvoice({
    id: invoiceId.value,
    invoice: { status: InvoiceStatus.CANCELED },
  });
  router.push({ name: 'invoiceList' });
};
</script>

<i18n lang="json">
{
  "en": {
    "invoice": "Invoice"
  },
  "de": {
    "invoice": "Rechnung"
  },
  "es": {
    "invoice": "Factura"
  }
}
</i18n>
